/**
 * HLS video player component for buffered streams
 * Shows all cameras simultaneously in a grid, synchronized playback
 */

import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  forwardRef,
} from 'react';
import Hls from 'hls.js';
import { clampSeekSeconds } from './viewer/seekClamp';
import { Volume2, Hourglass } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import api from '../services/api';
import PlayerChrome, { GlassRailData, PlayerTransport } from './PlayerChrome';
import { BufferedSession, BufferedCamera } from '../types';

type TierChoice = 'auto' | 'stream' | 'archive';

function appendTierQuery(url: string, tier: TierChoice): string {
  if (tier === 'auto') return url;
  // Preserve any pre-existing query string. The backend route accepts
  // `?tier=stream|archive|auto`; default `auto` keeps current behavior.
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}tier=${tier}`;
}

/**
 * Imperative handle exposed via ref. Used by the synced_viewer plugin
 * to seek video to an absolute UTC time the operator picked on the
 * map. Existing parent components don't need a ref — `forwardRef` is
 * non-breaking when the prop is omitted.
 */
export interface HLSPlayerHandle {
  /** Seek every camera to the moment whose absolute UTC matches `utcMs`. */
  seekToUtc: (utcMs: number) => void;
  play: () => void;
  pause: () => void;
}

// The glass rail's data shape lives with the chrome that renders it; re-exported
// here because every call site reaches it through the player.
export type { GlassRailData } from './PlayerChrome';

const normalizeRotation = (v?: number | null): 0 | 90 | 180 | 270 =>
  v === 90 || v === 180 || v === 270 ? v : 0;

interface HLSPlayerProps {
  session: BufferedSession;
  /** Fires whenever the main-audio camera's playhead moves; UTC ms. */
  onUtcUpdate?: (utcMs: number | null) => void;
  /** Fires once the earliest PROGRAM-DATE-TIME is known; UTC ms. */
  onPdtReady?: (firstPdtMs: number) => void;
  /**
   * VIDEO END of the main-audio camera as UTC ms — the newest playable
   * media (end of the seekable range) mapped through the segment PDT
   * anchor. The caller derives stream lag as `newest GPS fix − this`
   * (both phone-stamped, so client clock skew cancels), falling back
   * to wallclock when no fix exists. Deliberately NOT playhead-based:
   * an operator scrubbed 4 minutes back is not 4 minutes of lag
   * (2026-07-03 field feedback). Reports `null` until the first
   * PROGRAM-DATE-TIME is known (the viewer renders that as
   * "buffering"). Ticks at 1 Hz even while paused so lag chips age.
   * Meaningless for VOD playback (callers gate on liveness).
   */
  onVideoEdgeUpdate?: (videoEndUtcMs: number | null) => void;
  /** One-shot: seek every camera to this UTC the first time start
   * times are known. Subsequent prop changes are ignored — the user
   * is expected to drive playback after the initial seed. */
  initialSeekUtcMs?: number | null;
  /** 'full' (default — dashboard): session-id header, clock chips,
   *  LIVE/cam/buffer/HLS badges, tier picker. 'minimal': none of that
   *  — the public fan viewer must not expose session ids or internal
   *  buffer state, and its REPLAY/LIVE badge lives in FeedPlayer. */
  chrome?: 'full' | 'minimal';
  /** Height-bind the camera tiles (object-contain letterbox) WITHOUT
   *  entering browser fullscreen — for the mobile-landscape stage, which
   *  is short and wide so the default width-driven 16:9 box overflows.
   *  Same tile sizing as the fullscreen single-cam path, no chrome change. */
  heightFit?: boolean;
  /** Fires when the auto-hiding playbar shows/hides (landscape heightFit
   *  or fullscreen-fill). Lets the parent move sibling overlays (the
   *  telemetry HUD, driver label) in lockstep with the bar. Reports
   *  `false` whenever auto-hide isn't active. */
  onControlsHiddenChange?: (hidden: boolean) => void;
  /** Override the Maximise button: instead of fullscreening the player
   *  element (which hides everything around it), the caller takes a
   *  parent fullscreen — e.g. the mobile-landscape SHELL so nav / map /
   *  telemetry stay visible and the map can still be pulled out. When set,
   *  `isMaximized` drives the button's Maximise/Minimize icon. */
  onMaximize?: () => void;
  isMaximized?: boolean;
  /** Fullscreen-landscape glass telemetry rail (handover §3.6). When
   *  set, fullscreen also gains idle auto-hide chrome (the rail anchors
   *  to the playbar and settles into its place when chrome hides).
   *  Omitted (dashboard) = no rail, no auto-hide — behavior unchanged. */
  glassRail?: GlassRailData | null;
}

const CameraVideo: React.FC<{
  camera: BufferedCamera;
  tier: TierChoice;
  /** Session is actively recording — used to phrase the absent-tier
   * (404) message ("…during recording") for the live case. */
  isLive: boolean;
  /** 'minimal' hides the per-camera diagnostic chips (camera id, codec,
   *  audio, segment/buffer badges) — the public viewer's REPLAY/LIVE
   *  badge occupies the same corner and was overlapping them. */
  chrome: 'full' | 'minimal';
  onVideoRef: (video: HTMLVideoElement | null) => void;
  onHlsInstance: (hls: Hls | null) => void;
  onLoaded: () => void;
  onTimeUpdate: (time: number) => void;
  onDuration: (duration: number) => void;
  onStartOffset: (offsetMs: number) => void;
  isMuted: boolean;
  isMainAudio: boolean;
  /** Size the tile to FIT THE AVAILABLE HEIGHT (16:9 preserved, centered)
   *  instead of the default width-driven `paddingTop` box. Set for the
   *  single-camera fullscreen case: a landscape phone screen is
   *  height-constrained, so a width-derived box is far too tall and the
   *  video overflows/crops off the bottom (the "landscape video offset
   *  vertically" bug). */
  fitHeight?: boolean;
  /** Published server display-rotation (deg) for this session; the
   *  viewer applies it so fans see the operator's correction. */
  publishedRotation?: number;
  /** Operator-only: publish a new session rotation override. Undefined in
   *  the public viewer, where ↻ is an ephemeral personal nudge. */
  onPublishRotation?: (deg: 0 | 90 | 180 | 270) => void;
}> = ({
  camera,
  tier,
  isLive,
  chrome,
  onVideoRef,
  onHlsInstance,
  onLoaded,
  onTimeUpdate,
  onDuration,
  onStartOffset,
  isMuted,
  isMainAudio,
  fitHeight = false,
  publishedRotation = 0,
  onPublishRotation,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const networkRetryCount = useRef(0);
  const lastRetryTime = useRef(0);
  // C1 — per-segment PDT lookup. Populated from `details.fragments`
  // on every LEVEL_LOADED so the seg-0 anchor never depends on a
  // hardcoded segment-duration guess. See doc comment inside
  // LEVEL_LOADED handler below.
  const segmentPdtMap = useRef<Map<number, number>>(new Map());
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);
  // Native stream aspect (videoWidth/videoHeight). The public viewer
  // sizes its single tile to the REAL video instead of a hardcoded
  // 16:9 box — a 4:3 external cam used to float pillarboxed inside a
  // wider black rounded box ("player looks off on web"). Dashboard
  // grid tiles stay uniform 16:9.
  const [nativeAspect, setNativeAspect] = useState<number | null>(null);
  // Display-rotation correction. Seeded from the PUBLISHED server value
  // (session override -> device default -> 0, resolved server-side in
  // streams._resolve_session_rotation) so every fan sees the operator's
  // correction — no re-encode, the phone is never told. In the operator
  // dashboard the ↻ button publishes a new session override
  // (onPublishRotation); in the public viewer it is an ephemeral personal
  // nudge that resets on reload.
  const [rotationDeg, setRotationDeg] = useState<0 | 90 | 180 | 270>(() =>
    normalizeRotation(publishedRotation),
  );
  useEffect(() => {
    setRotationDeg(normalizeRotation(publishedRotation));
  }, [publishedRotation]);
  const cycleRotation = () => {
    setRotationDeg((prev) => {
      const next = (((prev + 90) % 360) as 0 | 90 | 180 | 270);
      onPublishRotation?.(next);
      return next;
    });
  };

  // Rotation-fit: rotating the container-sized <video> by 90/270 pushes its
  // bounding box past the overflow-hidden tile on the long axis and crops the
  // frame. Measure the tile and scale the rotated element by shortSide/longSide
  // so the rotated box fits exactly — correct for both the 16:9 default tile
  // and the fitHeight fullscreen box. 0/180 need no scale.
  const tileRef = useRef<HTMLDivElement>(null);
  const [tileBox, setTileBox] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;
    const measure = () => setTileBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const needsRotationFit = rotationDeg === 90 || rotationDeg === 270;
  const rotationFitScale =
    needsRotationFit && tileBox && tileBox.w > 0 && tileBox.h > 0
      ? Math.min(tileBox.w, tileBox.h) / Math.max(tileBox.w, tileBox.h)
      : 1;
  const rotationTransform = rotationDeg
    ? `rotate(${rotationDeg}deg)${needsRotationFit ? ` scale(${rotationFitScale})` : ''}`
    : undefined;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    onVideoRef(video);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const playlistUrl = appendTierQuery(camera.playlist_url, tier);

    if (camera.browser_playable === false) {
      const absolutePlaylist = `${window.location.origin}${playlistUrl}`;
      setLocalLoading(false);
      setLocalError(
        camera.playback_warning ||
          `This ${camera.video_codec?.toUpperCase() || 'HEVC'} stream is not browser-playable here. Use VLC/ffplay with: ${absolutePlaylist}`,
      );
      return () => {
        onVideoRef(null);
      };
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        // Playlist/segment routes are gated by the session's event
        // (public events serve anonymously; private/orphan sessions are
        // organizer/superadmin-only — Phase 5b, require_authz). hls.js
        // does its own fetches outside axios's withCredentials setting,
        // so it needs the SAME opt-in here: withCredentials=true makes
        // the browser attach the (HttpOnly) session cookie to these
        // same-origin XHRs too. No CSRF header needed — every request
        // hls.js makes is a GET (playlists/segments), a safe method
        // services/auth/csrf.py never gates. Anonymous fans have no
        // session cookie at all, so this is a harmless no-op for them —
        // same posture the old bearer injection had.
        xhrSetup: (xhr) => {
          xhr.withCredentials = true;
        },
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        startLevel: -1,
        debug: false,
        fragLoadingMaxRetry: 6,
        manifestLoadingMaxRetry: 4,
        levelLoadingMaxRetry: 4,
        // Live-edge tracking disabled — operator-requested. The previous
        // config (liveSyncDurationCount: 1, liveMaxLatencyDurationCount: 5)
        // aggressively pulled the play head to the live edge: opening a
        // still-recording session played segment 1, then jumped forward
        // to the latest segment as soon as the playlist refresh added
        // segments 2-N. Manual seek-back broke the auto-sync and let
        // sequential playback resume.
        //
        // With these values, hls.js treats the live playlist effectively
        // as VOD — starts at the beginning, plays through, never
        // force-seeks to the live edge. Trade-off: operators watching a
        // mid-recording session won't auto-catch up to "now"; a future
        // "Go to live" button can call `hls.media.currentTime =
        // hls.liveSyncPosition` when explicitly requested. The original
        // ~6 s lag goal for map-click "now" seeks is preserved by the
        // explicit seekToUtc imperative handle (which targets a UTC
        // moment, not the live edge).
        liveSyncDurationCount: 999,
        liveMaxLatencyDurationCount: 10000,
        startPosition: 0,
        // Tolerate the broken-tail .mp4 gap that the playlist generator
        // drops post-e25a386. Without this, hls.js stalls at the gap.
        maxBufferHole: 5,
      });

      hls.loadSource(playlistUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLocalLoading(false);
        setLocalError(null);
        onLoaded();
      });

      // C1 — Per-segment PDT lookup. Builds an authoritative map of
      // `segmentSn → programDateTime (UTC ms)` from the parsed playlist.
      // Replaces the old FRAG_LOADED arithmetic `fragTime - (fragSn *
      // 4000)` which hardcoded a 4 s segment duration (V2 is ~6.066 s;
      // accumulating ~2 s / segment of error).
      //
      // Anchor strategy: the "anchor" we report to `onStartOffset` is
      // the UTC corresponding to `video.currentTime = 0` — i.e. the
      // start of the playlist's media timeline. For the first playable
      // fragment `frag.start = 0` and `frag.programDateTime` IS that
      // anchor. For later fragments `frag.start > 0` is the media-time
      // offset of the fragment from playlist origin; subtracting
      // `frag.start * 1000` from its PDT gives the same playlist origin.
      //
      // Per Openpilot's EncodeIndex pattern: don't derive video-frame
      // time arithmetically when you can look it up explicitly. See
      // `docs/OPENPILOT_PATTERNS_REFERENCE.md` §2.6.
      //
      // Previous C1 implementation walked back from `earliestSn` to a
      // synthetic `sn=0` using `frag.duration * 1000` per step. That
      // was wrong: segments start at sn=1, not 0, so the walk-back
      // subtracted one extra segment-duration. Symptom (live 2026-05-13
      // on session ph-975ace-2026-05-12-2345): IMU's leftmost-sample
      // click sent video to 0:06 instead of 0:00; end of video came
      // 6 s short on the right. `frag.start` is the authoritative
      // media-time origin and avoids the off-by-one entirely.
      hls.on(Hls.Events.LEVEL_LOADED, (_, data) => {
        const details = data.details;
        if (!details || !details.fragments || details.fragments.length === 0) return;

        segmentPdtMap.current.clear();
        for (const frag of details.fragments) {
          if (frag.programDateTime && typeof frag.sn === 'number') {
            segmentPdtMap.current.set(frag.sn, new Date(frag.programDateTime).getTime());
          }
        }

        let anchorPdtMs: number | null = null;
        for (const frag of details.fragments) {
          if (frag.programDateTime && typeof frag.start === 'number') {
            anchorPdtMs = new Date(frag.programDateTime).getTime() - frag.start * 1000;
            break;
          }
        }
        if (anchorPdtMs != null) {
          onStartOffset(anchorPdtMs);
        }
      });

      hls.on(Hls.Events.FRAG_LOADED, (_, data) => {
        if (localError) setLocalError(null);
        networkRetryCount.current = 0;

        if (data.frag.programDateTime && typeof data.frag.sn === 'number') {
          segmentPdtMap.current.set(
            data.frag.sn,
            new Date(data.frag.programDateTime).getTime(),
          );
        }
        // Note: removed the hardcoded-4000 arithmetic seg-0 reconstruction
        // (was the C1 bug). LEVEL_LOADED's per-segment lookup above
        // supersedes it. Edge case where ONLY FRAG_LOADED ever fires
        // (impossibly old hls.js) is no longer supported.
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          // An explicitly-selected tier has no segments in the buffered
          // store (e.g. Preview/stream while a recording is live — the
          // stream tier may not be present as a buffered rendition), so
          // the playlist/segment request 404s. Treat that as an expected
          // absence, not a transient network fault: don't spin the
          // retry/backoff loop, just stop loading and show a clean,
          // tier-aware message. Mirrors the "404 = absence" handling in
          // `plugins/synced_viewer/api.ts`.
          const httpStatus =
            data.response?.code ??
            (data as unknown as { networkDetails?: { status?: number } })
              .networkDetails?.status;
          if (httpStatus === 404) {
            hls.stopLoad();
            setLocalLoading(false);
            const label =
              tier === 'stream'
                ? isLive
                  ? 'Preview unavailable during recording'
                  : 'Preview rendition not available'
                : tier === 'archive'
                  ? 'High Quality not ready yet'
                  : 'This rendition is not available';
            setLocalError(label);
            return;
          }
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR: {
              const MAX_NETWORK_RETRIES = 5;
              const now = Date.now();
              if (now - lastRetryTime.current > 30000) {
                networkRetryCount.current = 0;
              }
              if (networkRetryCount.current < MAX_NETWORK_RETRIES) {
                networkRetryCount.current++;
                lastRetryTime.current = now;
                const backoffMs = Math.min(
                  1000 * Math.pow(2, networkRetryCount.current - 1),
                  16000,
                );
                setTimeout(() => hls.startLoad(), backoffMs);
              } else {
                console.error(
                  `HLS network error for ${camera.camera_id}: max retries exceeded`,
                );
                setLocalError('Network error - reload to retry');
              }
              break;
            }
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              console.error(`HLS fatal error for ${camera.camera_id}:`, data.details);
              setLocalError(`Error: ${data.details}`);
              break;
          }
        }
      });

      hlsRef.current = hls;
      onHlsInstance(hls);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = playlistUrl;
      video.addEventListener('loadedmetadata', () => {
        setLocalLoading(false);
        onLoaded();
      });
    } else {
      setLocalError('HLS not supported');
    }

    const handleTimeUpdate = () => {
      onTimeUpdate(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      if (video.duration && isFinite(video.duration)) {
        onDuration(video.duration);
      }
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setNativeAspect(video.videoWidth / video.videoHeight);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('durationchange', handleLoadedMetadata);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('durationchange', handleLoadedMetadata);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
        onHlsInstance(null);
      }
      onVideoRef(null);
    };
  }, [
    camera.playlist_url,
    camera.camera_id,
    camera.browser_playable,
    camera.playback_warning,
    camera.video_codec,
    tier,
    isLive,
  ]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  return (
    <div
      ref={tileRef}
      className={cn(
        'relative overflow-hidden rounded bg-bg-video border-2',
        // The main-audio highlight border frames the VIDEO box. It is a
        // DASHBOARD diagnostic (which tile owns audio in a multi-cam
        // grid) — minimal chrome (public viewer, always single-cam)
        // must not draw a cyan ring around the fan's video. Also
        // dropped in fitHeight, where the box fills the whole padded
        // fullscreen area and the border would frame the screen edge.
        isMainAudio && !fitHeight && chrome === 'full'
          ? 'border-status-buffered'
          : 'border-transparent',
        // fitHeight: fill the available (height-bounded) area and let the
        // video's own object-contain letterbox 16:9, centered — correct
        // for a height-constrained landscape fullscreen. Default:
        // width-driven 16:9 (padding-top), unchanged for normal flow +
        // the dashboard multi-cam grid.
        fitHeight ? 'h-full w-full' : 'w-full',
      )}
      style={
        fitHeight
          ? undefined
          : {
              // Minimal chrome (public viewer, single tile): hug the
              // stream's real aspect once metadata lands — clamped to
              // square so a portrait clip can't blow the rail out
              // vertically (it letterboxes inside the square instead).
              // Rotated 90/270 keeps the 16:9 box the transform math
              // was built around. Dashboard grid: uniform 16:9 always.
              paddingTop:
                chrome === 'minimal' && nativeAspect != null && rotationDeg % 180 === 0
                  ? `${(100 / Math.max(1, nativeAspect)).toFixed(4)}%`
                  : '56.25%',
            }
      }
    >
      {localLoading && (
        <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center">
          <Spinner size={24} />
          <span className="mt-1 text-xs text-white">Loading {camera.camera_id}...</span>
        </div>
      )}

      {localError && (
        <div className="absolute inset-0 z-[2] flex items-center justify-center bg-black/80">
          <span className="px-2 text-xs text-status-error">{localError}</span>
        </div>
      )}

      <video
        ref={videoRef}
        muted={isMuted}
        playsInline
        className="absolute inset-0 h-full w-full object-contain"
        style={{
          transform: rotationTransform,
          transformOrigin: 'center center',
        }}
      />

      {/* Rotation toggle — cycles 0 → 90 → 180 → 270 → 0. */}
      <div className="absolute right-2 top-2 z-[4]">
        <button
          onClick={cycleRotation}
          className={cn(
            'rounded-full border bg-black/70 px-2 py-0.5 font-mono text-xs hover:bg-black/85',
            rotationDeg ? 'border-accent-purple text-accent-purple' : 'border-white/20 text-white',
          )}
        >
          ↻ {rotationDeg}°
        </button>
      </div>

      {/* Camera label overlay — dashboard diagnostics; hidden in
          minimal chrome (the viewer's REPLAY/LIVE badge owns that
          corner and was overlapping these). */}
      {chrome === 'full' && (
        <div className="absolute left-2 top-2 z-[3] flex gap-1">
          <Badge className="bg-black/70 font-bold text-white hover:bg-black/70">
            {camera.camera_id}
          </Badge>
          {camera.video_codec && (
            <Badge
              variant={camera.browser_playable === false ? 'destructive' : 'secondary'}
              className="text-[10px]"
            >
              {camera.video_codec.toUpperCase()}
            </Badge>
          )}
          {isMainAudio && (
            <Badge className="flex items-center gap-1 bg-status-buffered/90 text-[10px] text-white">
              <Volume2 className="h-3 w-3" />
              Audio
            </Badge>
          )}
        </div>
      )}

      {/* Buffer status — internal diagnostics, dashboard only. */}
      {chrome === 'full' && (
        <div className="absolute bottom-2 right-2 z-[3] flex gap-1">
          <Badge className="bg-black/70 text-[10px] text-white hover:bg-black/70">
            {camera.segment_count} seg
          </Badge>
          <Badge
            className={cn(
              'text-[10px]',
              camera.is_buffer_ready
                ? 'bg-status-online/15 text-status-online'
                : 'bg-status-warning/15 text-status-warning',
            )}
          >
            {camera.is_buffer_ready ? 'Ready' : `${camera.buffer_percent}%`}
          </Badge>
        </div>
      )}
    </div>
  );
};

export const HLSPlayer = forwardRef<HLSPlayerHandle, HLSPlayerProps>(
  (
    {
      session,
      onUtcUpdate,
      onPdtReady,
      onVideoEdgeUpdate,
      initialSeekUtcMs,
      chrome = 'full',
      heightFit = false,
      onControlsHiddenChange,
      onMaximize,
      isMaximized = false,
      glassRail = null,
    },
    forwardedRef,
  ) => {
    const videoRefs = useRef<Map<string, HTMLVideoElement | null>>(new Map());
    const hlsRefs = useRef<Map<string, Hls | null>>(new Map());
    const cameraStartTimes = useRef<Map<string, number>>(new Map());

    // Operator-pickable rendition. `auto` keeps the historical
    // dedup-prefer-archive behavior; `stream` / `archive` force the
    // backend to filter by extension. Persisted per session_id so a
    // reload preserves the choice. On change, the per-camera
    // `useEffect` keyed on `[playlist_url, tier]` tears down + rebuilds
    // each Hls instance with the new `?tier=` URL.
    const tierStorageKey = `yelve.viewer.tier.${session.session_id}`;
    const [tier, setTier] = useState<TierChoice>(() => {
      try {
        const raw = localStorage.getItem(tierStorageKey);
        if (raw === 'stream' || raw === 'archive' || raw === 'auto') return raw;
      } catch {
        /* ignore */
      }
      return 'auto';
    });
    useEffect(() => {
      try {
        localStorage.setItem(tierStorageKey, tier);
      } catch {
        /* ignore */
      }
    }, [tier, tierStorageKey]);

    // The stream/Preview rendition may be absent from the buffered store
    // while a recording is live, so its playlist 404s. The Preview toggle
    // is hidden while live; this also demotes a persisted `stream`
    // selection back to `auto` (which serves whatever tier is present) so
    // a reload mid-recording doesn't land on an empty tier.
    useEffect(() => {
      if (session.is_active && tier === 'stream') {
        setTier('auto');
      }
    }, [session.is_active, tier]);

    const [isPlaying, setIsPlaying] = useState(false);
    const [mainAudioCamera, setMainAudioCamera] = useState<string>(
      session.cameras[0]?.camera_id || '',
    );
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const [loadedCount, setLoadedCount] = useState(0);
    const [startTimesReceived, setStartTimesReceived] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [isMuted, setIsMuted] = useState(false);

    const allLoaded = loadedCount >= session.cameras.length;

    // ── HEVC cold-decoder kick ──────────────────────────────────────
    // The master playlist advertises `avc1.640028` (a load-bearing lie
    // so hls.js's MSE codec gate passes — the BARE `"hvc1"` string is
    // rejected by isTypeSupported, though the fully-qualified
    // `hvc1.1.6.L93.B0` is supported). hls.js therefore creates the
    // video SourceBuffer as AVC and feeds it HEVC. On the first play of
    // a session Chrome's decoder, configured for AVC, STALLS on cold
    // start: it emits no video frames for ~3-4 s while the (instantly
    // decodable) AAC audio plays on — the "audio leads video" report.
    //
    // A seek resets the media element's decode pipeline (flush +
    // re-decode from the nearest sync sample), which is exactly why the
    // manual fix — dragging the seek bar — restores sync every time.
    // We automate that one corrective seek on the first play of a
    // session. (PR #17's currentTime nudge and PR #18's
    // requestVideoFrameCallback gate both failed: a paused element
    // never fires rVFC for an already-painted frame, so the gate just
    // burned its timeout and played cold — verified live in Chrome.)
    //
    // Root-cause fix is to advertise the real `hvc1` codec so the
    // decoder is configured correctly from frame 0; deferred because it
    // re-treads the codec-detection path that broke playback before.
    const firstPlayKickRef = useRef(false);
    useEffect(() => {
      firstPlayKickRef.current = false;
    }, [session.session_id, tier]);

    const kickDecoderOnce = useCallback(() => {
      if (firstPlayKickRef.current) return;
      firstPlayKickRef.current = true;
      const main = videoRefs.current.get(mainAudioCamera);
      if (!main) return;
      // Let the cold decoder stall first, then force a REAL seek — a
      // sub-frame nudge won't trigger a decoder flush. ~0.4 s is below
      // typical content interest and is re-synced immediately (audio
      // follows the single shared currentTime to the same point).
      window.setTimeout(() => {
        try {
          if (!main.paused) main.currentTime = main.currentTime + 0.4;
        } catch {
          /* ignore */
        }
      }, 350);
    }, [mainAudioCamera]);

    const lastReportedUtcRef = useRef<number | null>(null);
    useEffect(() => {
      if (!onUtcUpdate) return;
      const mainStart = cameraStartTimes.current.get(mainAudioCamera);
      if (mainStart === undefined || mainStart === 0) {
        if (lastReportedUtcRef.current !== null) {
          lastReportedUtcRef.current = null;
          onUtcUpdate(null);
        }
        return;
      }
      const utcMs = mainStart + currentTime * 1000;
      const raf = requestAnimationFrame(() => {
        if (lastReportedUtcRef.current !== utcMs) {
          lastReportedUtcRef.current = utcMs;
          onUtcUpdate(utcMs);
        }
      });
      return () => cancelAnimationFrame(raf);
      // startTimesReceived: on mount this effect runs BEFORE the first
      // PDT is parsed (mainStart undefined → null) and currentTime
      // stays 0 until playback/seek, so a never-played page reported
      // no playhead forever — the /play trim card's "at playhead"
      // buttons stayed disabled (2026-07-03 field: 'didn't delete
      // after playhead'). PDT arrival now re-fires the report, so a
      // paused-at-start player anchors to the session start.
    }, [currentTime, mainAudioCamera, onUtcUpdate, startTimesReceived]);

    useEffect(() => {
      if (!onPdtReady) return;
      if (startTimesReceived === 0) return;
      const earliest = Math.min(...Array.from(cameraStartTimes.current.values()));
      if (Number.isFinite(earliest) && earliest > 0) {
        onPdtReady(earliest);
      }
    }, [startTimesReceived, onPdtReady]);

    // Video-edge metric for the fan viewer (see prop doc). 1 Hz
    // interval — independent of timeupdate so a paused/stalled player
    // keeps reporting instead of freezing the chip. Reads the live
    // <video> element directly to avoid re-running on every
    // currentTime render. The END of the seekable range is the newest
    // media hls.js has from the playlist — "what you could watch if
    // you jumped to live" — so scrubbing back never inflates the
    // number (the old playhead-based lag read a 4-minute scrub as 4 m
    // of stream lag).
    useEffect(() => {
      if (!onVideoEdgeUpdate) return;
      const tick = () => {
        const mainStart = cameraStartTimes.current.get(mainAudioCamera);
        const video = videoRefs.current.get(mainAudioCamera);
        if (mainStart === undefined || mainStart === 0 || !video) {
          onVideoEdgeUpdate(null);
          return;
        }
        const seekable = video.seekable;
        const endMediaS =
          seekable.length > 0 ? seekable.end(seekable.length - 1) : video.currentTime;
        onVideoEdgeUpdate(mainStart + endMediaS * 1000);
      };
      tick();
      const id = window.setInterval(tick, 1000);
      return () => {
        window.clearInterval(id);
        onVideoEdgeUpdate(null);
      };
    }, [onVideoEdgeUpdate, mainAudioCamera, session.session_id]);

    // One-shot initial seed for the Compare flow: when the parent
    // mounts this player with an initialSeekUtcMs, wait until at least
    // one camera reports its PROGRAM-DATE-TIME (so seekToUtc can map
    // wall-clock → media time), then seek every camera once. A ref
    // gates the effect so a prop reference change can't trigger a
    // second seed mid-playback.
    const initialSeekDoneRef = useRef(false);
    useEffect(() => {
      if (initialSeekDoneRef.current) return;
      if (initialSeekUtcMs == null) return;
      if (startTimesReceived === 0) return;
      const earliestStart = Math.min(...Array.from(cameraStartTimes.current.values()));
      if (!Number.isFinite(earliestStart) || earliestStart <= 0) return;
      const refStart = cameraStartTimes.current.get(mainAudioCamera) || earliestStart;
      const refVideo = videoRefs.current.get(mainAudioCamera);
      // Clamp to what's actually seekable: a resume point can have rolled off
      // a live window or land past the end — Math.max(0, …) alone would seek
      // to buffer start (oldest segment) or past the end. See seekClamp.
      const refSeekSec = refVideo
        ? clampSeekSeconds(refVideo, (initialSeekUtcMs - refStart) / 1000, {
            live: session.is_active,
            intent: 'resume',
          })
        : Math.max(0, (initialSeekUtcMs - refStart) / 1000);
      if (refVideo) refVideo.currentTime = refSeekSec;
      videoRefs.current.forEach((video, cameraId) => {
        if (!video || cameraId === mainAudioCamera) return;
        const camStart = cameraStartTimes.current.get(cameraId) || earliestStart;
        video.currentTime = clampSeekSeconds(video, (initialSeekUtcMs - camStart) / 1000, {
          live: session.is_active,
          intent: 'resume',
        });
      });
      setCurrentTime(refSeekSec);
      initialSeekDoneRef.current = true;
    }, [initialSeekUtcMs, startTimesReceived, mainAudioCamera, session.is_active]);

    useImperativeHandle(
      forwardedRef,
      () => ({
        seekToUtc: (utcMs: number) => {
          const earliestStart = Math.min(...Array.from(cameraStartTimes.current.values()));
          if (!Number.isFinite(earliestStart) || earliestStart <= 0) return;
          const refStart = cameraStartTimes.current.get(mainAudioCamera) || earliestStart;
          const refVideo = videoRefs.current.get(mainAudioCamera);
          const refSeekSec = refVideo
            ? clampSeekSeconds(refVideo, (utcMs - refStart) / 1000, {
                live: session.is_active,
                intent: 'explicit',
              })
            : Math.max(0, (utcMs - refStart) / 1000);
          if (refVideo) refVideo.currentTime = refSeekSec;
          videoRefs.current.forEach((video, cameraId) => {
            if (!video || cameraId === mainAudioCamera) return;
            const camStart = cameraStartTimes.current.get(cameraId) || earliestStart;
            video.currentTime = clampSeekSeconds(video, (utcMs - camStart) / 1000, {
              live: session.is_active,
              intent: 'explicit',
            });
          });
          setCurrentTime(refSeekSec);
        },
        play: () => {
          videoRefs.current.forEach((v) => v?.play().catch(() => undefined));
          setIsPlaying(true);
          // Kick the cold HEVC decoder on first play (spacebar /
          // map-click path). See kickDecoderOnce.
          kickDecoderOnce();
        },
        pause: () => {
          videoRefs.current.forEach((v) => v?.pause());
          setIsPlaying(false);
        },
      }),
      [mainAudioCamera, kickDecoderOnce, session.is_active],
    );

    useEffect(() => {
      const mainVideo = videoRefs.current.get(mainAudioCamera);
      if (mainVideo) {
        mainVideo.volume = isMuted ? 0 : volume;
      }
    }, [volume, isMuted, mainAudioCamera]);

    const getEarliestStartTime = useCallback(() => {
      const times = Array.from(cameraStartTimes.current.values());
      return times.length > 0 ? Math.min(...times) : 0;
    }, []);

    const syncAllCameras = useCallback(() => {
      const earliestStart = getEarliestStartTime();
      if (earliestStart === 0) return;
      const refVideo = videoRefs.current.get(mainAudioCamera);
      if (!refVideo) return;
      const refStart = cameraStartTimes.current.get(mainAudioCamera) || earliestStart;
      const refCurrentTime = refVideo.currentTime;
      const realWorldTime = refStart + refCurrentTime * 1000;

      videoRefs.current.forEach((video, cameraId) => {
        if (video && cameraId !== mainAudioCamera) {
          const cameraStart = cameraStartTimes.current.get(cameraId) || earliestStart;
          const targetTime = (realWorldTime - cameraStart) / 1000;
          if (targetTime >= 0 && Math.abs(video.currentTime - targetTime) > 0.1) {
            video.currentTime = targetTime;
          }
        }
      });
    }, [getEarliestStartTime, mainAudioCamera]);

    useEffect(() => {
      if (!isPlaying) return;
      const interval = setInterval(() => {
        syncAllCameras();
      }, 5000);
      return () => clearInterval(interval);
    }, [isPlaying, syncAllCameras]);

    const [hasInitialSync, setHasInitialSync] = useState(false);
    useEffect(() => {
      if (hasInitialSync) return;
      if (startTimesReceived >= session.cameras.length && allLoaded) {
        setTimeout(() => {
          const times = Array.from(cameraStartTimes.current.values());
          const earliestStart = Math.min(...times);
          const latestStart = Math.max(...times);
          const offsetToSync = (latestStart - earliestStart) / 1000;
          const refVideo = videoRefs.current.get(mainAudioCamera);
          if (refVideo && offsetToSync > 0.1) {
            const refStart = cameraStartTimes.current.get(mainAudioCamera) || earliestStart;
            const refOffset = (refStart - earliestStart) / 1000;
            const seekTo = offsetToSync - refOffset + 0.5;
            refVideo.currentTime = seekTo;
          }
          setTimeout(() => {
            syncAllCameras();
            setHasInitialSync(true);
          }, 200);
        }, 500);
      }
    }, [
      allLoaded,
      session.cameras.length,
      syncAllCameras,
      hasInitialSync,
      startTimesReceived,
      mainAudioCamera,
    ]);

    const handlePlayPause = useCallback(() => {
      const videos = Array.from(videoRefs.current.values()).filter(Boolean) as HTMLVideoElement[];
      if (isPlaying) {
        videos.forEach((video) => video.pause());
        setIsPlaying(false);
      } else {
        syncAllCameras();
        Promise.all(videos.map((video) => video.play().catch(() => {}))).then(() => {
          setIsPlaying(true);
          // First play of a session: kick the cold HEVC decoder with a
          // corrective seek so audio doesn't lead video (see
          // kickDecoderOnce). No-op on every play after the first.
          kickDecoderOnce();
        });
      }
    }, [isPlaying, syncAllCameras, kickDecoderOnce]);

    const handleSeek = useCallback(
      (vals: number[]) => {
        const seekTime = vals[0];
        setCurrentTime(seekTime);
        setIsSeeking(true);
        const earliestStart = getEarliestStartTime();
        videoRefs.current.forEach((video, cameraId) => {
          if (video) {
            const cameraStart = cameraStartTimes.current.get(cameraId) || earliestStart;
            const offsetSeconds = (cameraStart - earliestStart) / 1000;
            const adjustedTime = Math.max(0, seekTime - offsetSeconds);
            video.currentTime = adjustedTime;
          }
        });
        setTimeout(() => setIsSeeking(false), 100);
      },
      [getEarliestStartTime],
    );

    const transport: PlayerTransport = {
      isPlaying,
      currentTime,
      duration,
      ready: allLoaded,
      muted: isMuted,
      volume,
      togglePlay: handlePlayPause,
      seek: (seconds) => handleSeek([seconds]),
      setMuted: setIsMuted,
      // Raising the volume off zero implies unmuting — otherwise the slider
      // moves and nothing is heard.
      setVolume: (next) => {
        setVolume(next);
        if (next > 0 && isMuted) setIsMuted(false);
      },
    };

    const gridColsClass = (() => {
      const count = session.cameras.length;
      if (count === 1) return 'sm:grid-cols-1';
      if (count === 2) return 'sm:grid-cols-2';
      if (count <= 4) return 'sm:grid-cols-2';
      return 'sm:grid-cols-3';
    })();

    return (
      <Card className="flex h-full flex-col">
        <CardContent
          className={cn(
            'flex flex-grow flex-col p-2 sm:p-4',
            // Height-fit (mobile landscape): let the column shrink so the
            // video letterboxes to the short stage instead of overflowing.
            // Fullscreen bounds via the chrome's own h-screen, which does not
            // depend on this chain.
            heightFit && session.cameras.length === 1 && 'min-h-0',
          )}
        >
          {/* Header — hidden in minimal chrome (public viewer: no
              session ids / internal buffer chips). */}
          {chrome === 'full' && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h2 className="min-w-0 flex-1 truncate text-sm font-semibold sm:text-xl">
              {session.session_id}
            </h2>
            <div className="flex shrink-0 items-center gap-1">
              {/* B3 — digital clock overlay. Always-visible diagnostic
                  showing playhead position (mm:ss.SSS, video-local) +
                  wall-clock UTC (mm:ss.SSS, derived from segment-0 PDT
                  + currentTime). Makes pane-vs-video drift visible
                  immediately — click an IMU peak, watch the displayed
                  UTC update, compare to the IMU sample's UTC bucket.
                  Without this overlay, drift was only diagnosable by
                  diving into chunks.sqlite + sidecar.json. */}
              <div
                className="flex flex-col whitespace-nowrap rounded border border-border px-2 py-0.5 font-mono text-[0.65rem] leading-tight text-fg-secondary sm:text-xs"
                title="player time / wall-clock UTC at playhead"
              >
                <span>
                  {(() => {
                    const totalMs = Math.max(0, currentTime * 1000);
                    const mm = Math.floor(totalMs / 60000);
                    const ss = Math.floor((totalMs % 60000) / 1000);
                    const ms = Math.floor(totalMs % 1000);
                    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
                  })()}
                </span>
                <span className="text-fg-muted">
                  {(() => {
                    const mainStart = cameraStartTimes.current.get(mainAudioCamera);
                    if (mainStart === undefined || mainStart === 0) return 'no PDT';
                    const utcMs = mainStart + currentTime * 1000;
                    const d = new Date(utcMs);
                    const hh = String(d.getUTCHours()).padStart(2, '0');
                    const mm = String(d.getUTCMinutes()).padStart(2, '0');
                    const ss = String(d.getUTCSeconds()).padStart(2, '0');
                    const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
                    return `${hh}:${mm}:${ss}.${ms}Z`;
                  })()}
                </span>
              </div>
              <Badge
                className={
                  session.is_active
                    ? 'bg-status-error/15 text-status-error'
                    : 'bg-bg-elevated text-fg-muted'
                }
              >
                {session.is_active ? 'LIVE' : 'ENDED'}
              </Badge>
              <Badge variant="outline" className="border-accent text-accent">
                {session.cameras.length} cam
              </Badge>
              {session.is_buffer_ready ? (
                <Badge className="bg-status-online/15 text-status-online">Buffer Ready</Badge>
              ) : (
                <Badge className="flex items-center gap-1 bg-status-warning/15 text-status-warning">
                  <Hourglass className="h-3 w-3" />
                  Buffering ({session.buffer_segments_required} req)
                </Badge>
              )}
              <Badge variant="outline" className="hidden sm:inline-flex border-status-buffered text-status-buffered">
                HLS
              </Badge>
              {/* Operator-pickable rendition. Default `auto` matches
                  the existing dedup-prefer-archive behavior; explicit
                  Preview / Archive bypass the dedup by query-string.
                  Preview (stream tier) is hidden while the session is
                  live — the stream rendition may be absent from the
                  buffered store during a recording, so its playlist
                  404s; `auto` still serves whatever is present. */}
              <ToggleGroup
                type="single"
                size="sm"
                value={tier}
                onValueChange={(v) => {
                  if (v === 'auto' || v === 'stream' || v === 'archive') setTier(v);
                }}
                className="ml-1"
              >
                <ToggleGroupItem value="auto" aria-label="auto tier" className="px-2 text-[10px]">
                  Auto
                </ToggleGroupItem>
                {!session.is_active && (
                  <ToggleGroupItem value="stream" aria-label="low quality tier" className="px-2 text-[10px]">
                    Low quality
                  </ToggleGroupItem>
                )}
                <ToggleGroupItem value="archive" aria-label="high quality tier" className="px-2 text-[10px]">
                  High quality
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
          )}

          {/* Camera grid inside the shared chrome (which owns the fullscreen
              container, the playbar and the glass rail). */}
          <PlayerChrome
            transport={transport}
            variant={chrome}
            singleSurface={session.cameras.length === 1}
            heightFit={heightFit}
            glassRail={glassRail}
            onMaximize={onMaximize}
            isMaximized={isMaximized}
            onControlsHiddenChange={onControlsHiddenChange}
          >
            {({ fitTiles }) => (
            <div
              className={cn(
                'flex-grow',
                // Single-cam fullscreen (the fan-viewer / glass-rail
                // landscape case): bound the tile by HEIGHT (h-full
                // chain → video object-contain centers it) instead of
                // scrolling a too-tall width-driven box. Multi-cam +
                // non-fullscreen keep the scroll grid.
                fitTiles ? 'min-h-0 overflow-hidden' : 'overflow-auto',
              )}
            >
              <div
                className={cn(
                  'grid grid-cols-1 gap-2',
                  gridColsClass,
                  fitTiles && 'h-full min-h-0',
                )}
              >
                {session.cameras.map((camera) => (
                  <div
                    key={camera.camera_id}
                    onClick={() => setMainAudioCamera(camera.camera_id)}
                    className={cn(
                      'cursor-pointer',
                      fitTiles && 'h-full min-h-0',
                    )}
                  >
                    <CameraVideo
                      camera={camera}
                      tier={tier}
                      isLive={session.is_active}
                      chrome={chrome}
                      fitHeight={fitTiles}
                      onVideoRef={(video) => videoRefs.current.set(camera.camera_id, video)}
                      onHlsInstance={(hls) => hlsRefs.current.set(camera.camera_id, hls)}
                      onLoaded={() => setLoadedCount((c) => c + 1)}
                      onTimeUpdate={(time) => {
                        if (camera.camera_id === mainAudioCamera && !isSeeking) {
                          setCurrentTime(time);
                        }
                      }}
                      onDuration={(d) => {
                        if (d > duration) setDuration(d);
                      }}
                      onStartOffset={(epochMs) => {
                        if (!cameraStartTimes.current.has(camera.camera_id)) {
                          cameraStartTimes.current.set(camera.camera_id, epochMs);
                          setStartTimesReceived((c) => c + 1);
                        }
                      }}
                      publishedRotation={session.rotation ?? 0}
                      onPublishRotation={
                        chrome === 'full'
                          ? (deg) => {
                              void api.setSessionRotation(session.session_id, deg);
                            }
                          : undefined
                      }
                      isMuted={camera.camera_id !== mainAudioCamera}
                      isMainAudio={camera.camera_id === mainAudioCamera}
                    />
                  </div>
                ))}
              </div>
            </div>
            )}
          </PlayerChrome>
        </CardContent>
      </Card>
    );
  },
);

HLSPlayer.displayName = 'HLSPlayer';

export default HLSPlayer;
